/**
 * =============================================================================
 * MEDIA SETTINGS CARD (ADR-022 §10.1 — PR4b, BL-IMG-ADMIN-AUTHORING-001)
 * =============================================================================
 *
 * Tenant-facing control for the §10.1 image-attachment settings, mounted next
 * to LaunchModeCard and following its exact contract: reads
 * `GET /api/admin/settings`, sends a DIRTY-FIELDS-ONLY `PUT` (an
 * unchanged field is never re-sent, so the server never re-writes it), and
 * re-seeds the form from the response — the worker returns EFFECTIVE
 * (resolver-clamped) values, so what renders is always what is enforced.
 *
 * Client-side bounds mirror the server's resolver bounds via the
 * FILE_VALIDATION constants (clamps only ever TIGHTEN toward the platform
 * ceilings); they are UX only — the strict per-field PUT validation is the
 * gate. The D-QUOTA usage block is READ-ONLY (the ceiling is a platform
 * constant; the tenant-tunable override is deferred, owner 2026-07-04).
 *
 * Postures govern CUSTOMER uploads only — staff uploads are author-trust
 * auto-approved (ADR-022 §5) and ignore `imageModeration` entirely; the card
 * says so rather than implying a staff review queue exists.
 * =============================================================================
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FlexItem,
  FormGroup,
  Input,
  Panel,
  Select,
  Switch,
  Text,
} from '@bigcommerce/big-design';
import {
  FILE_VALIDATION,
  IMAGE_MODERATION_POSTURES,
  resolveMediaSettings,
} from '@find-my-part/types';
import type {
  AllowedMimeType,
  ImageModerationPosture,
  StoreSettingsResponse,
  StoreSettingsPatch,
  StoreSettingsConflict,
  StoreSettingsExpectedState,
  StoreSettingsWriteField,
  TenantMediaUsage,
} from '@find-my-part/types';
import { useSession } from '../../hooks/useSession';
import { useToast } from '../../hooks/useToast';
import { useTenantSettings } from '../../providers/TenantSettingsProvider';
import {
  isAmbiguousSettingsFailure,
  readSettingsConflict,
  selectUsableSettingsExpectedState,
  SettingsConflictNotice,
} from './SettingsConflictNotice';

/** Short display labels for the five platform MIME types. */
const FORMAT_LABELS: Record<AllowedMimeType, string> = {
  'image/jpeg': 'JPEG',
  'image/png': 'PNG',
  'image/webp': 'WebP',
  'image/heic': 'HEIC',
  'image/heif': 'HEIF',
};

const POSTURE_LABELS: Record<ImageModerationPosture, string> = {
  REQUIRE_APPROVAL: 'Require approval',
  AUTO_APPROVE: 'Auto-approve',
  DISABLED: 'Disabled',
};

const POSTURE_DESCRIPTIONS: Record<ImageModerationPosture, string> = {
  REQUIRE_APPROVAL: 'Customer images stay hidden until a moderator approves them.',
  AUTO_APPROVE: 'Customer images are published immediately without review.',
  DISABLED: 'Customer image uploads are rejected entirely.',
};

const POSTURE_OPTIONS = IMAGE_MODERATION_POSTURES.map((posture) => ({
  value: posture,
  content: POSTURE_LABELS[posture],
}));

/** The four editable numeric fields and their resolver bounds (§10.1). */
const NUMERIC_FIELDS = [
  {
    key: 'maxImagesPerSubmission',
    label: 'Max images per question',
    min: 1,
    max: FILE_VALIDATION.MAX_IMAGES_PER_QUESTION,
    help: undefined as string | undefined,
  },
  {
    key: 'maxImagesPerReply',
    label: 'Max images per customer reply',
    min: 0,
    max: FILE_VALIDATION.MAX_IMAGES_PER_REPLY,
    help: '0 disables customer reply images',
  },
  {
    key: 'maxImagesPerStaffReply',
    label: 'Max images per staff reply',
    min: 0,
    max: FILE_VALIDATION.MAX_IMAGES_PER_STAFF_REPLY,
    help: undefined,
  },
  {
    key: 'maxImageSizeMb',
    label: 'Max image size (MB)',
    min: 1,
    max: FILE_VALIDATION.MAX_SIZE_MB,
    help: undefined,
  },
] as const;

type NumericFieldKey = (typeof NUMERIC_FIELDS)[number]['key'];

/**
 * Form snapshot of the editable media fields. Numeric fields are kept as
 * STRINGS while editing (an empty/partial value must not coerce to 0 and
 * silently disable customer reply images); parsing happens at validation.
 */
export interface MediaFormState {
  imageAttachmentsEnabled: boolean;
  maxImagesPerSubmission: string;
  maxImagesPerReply: string;
  maxImagesPerStaffReply: string;
  maxImageSizeMb: string;
  allowedImageFormats: AllowedMimeType[];
  imageModeration: ImageModerationPosture;
}

/** Seed the form from the server's EFFECTIVE (resolver-clamped) values. */
function formStateFrom(settings: StoreSettingsResponse): MediaFormState {
  return {
    imageAttachmentsEnabled: settings.imageAttachmentsEnabled,
    maxImagesPerSubmission: String(settings.maxImagesPerSubmission),
    maxImagesPerReply: String(settings.maxImagesPerReply),
    maxImagesPerStaffReply: String(settings.maxImagesPerStaffReply),
    maxImageSizeMb: String(settings.maxImageSizeMb),
    // Preserve platform order for a deterministic dirty comparison.
    allowedImageFormats: FILE_VALIDATION.ALLOWED_MIME_TYPES.filter((m) =>
      settings.allowedImageFormats.includes(m)
    ),
    imageModeration: settings.imageModeration,
  };
}

/** Parse an integer field; null when not a within-bounds integer. */
function parseBoundedInt(value: string, min: number, max: number): number | null {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const parsed = Number(trimmed);
  return parsed >= min && parsed <= max ? parsed : null;
}

/**
 * Build the dirty-fields-only PUT payload from the persisted snapshot vs the
 * edited draft (fold S4 — the ONE comparison both the Save handler and the
 * `dirty` button gate share; previously two hand-maintained copies that could
 * drift). A key is included only when it differs; `allowedImageFormats` is
 * compared by VALUE (both sides are platform-order-filtered by
 * `formStateFrom`/`toggleFormat`, so ordered join equality IS deep equality —
 * reference equality would mark every re-seeded array dirty). A
 * dirty-but-out-of-bounds numeric sets `invalid` instead of a payload key
 * (the Save gate disables on it; the handler treats it as a no-op guard).
 */
export function buildDirtyPayload(
  current: MediaFormState,
  draft: MediaFormState
): { payload: StoreSettingsPatch; invalid: boolean } {
  const payload: StoreSettingsPatch = {};
  let invalid = false;
  if (draft.imageAttachmentsEnabled !== current.imageAttachmentsEnabled) {
    payload.imageAttachmentsEnabled = draft.imageAttachmentsEnabled;
  }
  for (const field of NUMERIC_FIELDS) {
    if (draft[field.key] !== current[field.key]) {
      const parsed = parseBoundedInt(draft[field.key], field.min, field.max);
      if (parsed === null) {
        invalid = true;
      } else {
        payload[field.key] = parsed;
      }
    }
  }
  if (draft.allowedImageFormats.join(',') !== current.allowedImageFormats.join(',')) {
    payload.allowedImageFormats = draft.allowedImageFormats;
  }
  if (draft.imageModeration !== current.imageModeration) {
    payload.imageModeration = draft.imageModeration;
  }
  return { payload, invalid };
}

/** Raw write value represented by the current controlled form field. */
function mediaDraftValue(draft: MediaFormState, field: StoreSettingsWriteField): unknown {
  switch (field) {
    case 'imageAttachmentsEnabled':
      return draft.imageAttachmentsEnabled;
    case 'maxImagesPerSubmission':
      return (
        parseBoundedInt(draft.maxImagesPerSubmission, 1, FILE_VALIDATION.MAX_IMAGES_PER_QUESTION) ??
        draft.maxImagesPerSubmission
      );
    case 'maxImagesPerReply':
      return (
        parseBoundedInt(draft.maxImagesPerReply, 0, FILE_VALIDATION.MAX_IMAGES_PER_REPLY) ??
        draft.maxImagesPerReply
      );
    case 'maxImagesPerStaffReply':
      return (
        parseBoundedInt(
          draft.maxImagesPerStaffReply,
          0,
          FILE_VALIDATION.MAX_IMAGES_PER_STAFF_REPLY
        ) ?? draft.maxImagesPerStaffReply
      );
    case 'maxImageSizeMb':
      return (
        parseBoundedInt(draft.maxImageSizeMb, 1, FILE_VALIDATION.MAX_SIZE_MB) ??
        draft.maxImageSizeMb
      );
    case 'allowedImageFormats':
      return draft.allowedImageFormats;
    case 'imageModeration':
      return draft.imageModeration;
    default:
      return undefined;
  }
}

/** Human-readable byte total for the usage block (MB below 1 GB, else GB). */
export function formatBytesHuman(bytes: number): string {
  const GB = 1024 * 1024 * 1024;
  const MB = 1024 * 1024;
  if (bytes >= GB) {
    const value = bytes / GB;
    return `${Number.isInteger(value) ? value : value.toFixed(1)} GB`;
  }
  const value = bytes / MB;
  return `${Number.isInteger(value) ? value : value.toFixed(1)} MB`;
}

export interface MediaSettingsCardProps {
  /**
   * ADR-025 B-2 (§3.1.4 — `BL-ADMIN-M1-EXPIRY-DIRTY-GUARD-001`): unsaved-edit
   * reporting to the shell. Fired on TRANSITIONS only (clean→dirty /
   * dirty→clean), never per keystroke/re-render. "Dirty" reuses the card's
   * existing `buildDirtyPayload` semantics — any changed key, INCLUDING a
   * changed-but-invalid numeric (that draft is still an unsaved edit the
   * operator would lose). OPTIONAL: the flag-OFF legacy layout passes nothing
   * and the card behaves exactly as before (rollback surface, M9).
   */
  onDirtyChange?: (dirty: boolean) => void;
}

/**
 * Admin card for viewing and changing the tenant §10.1 media settings.
 */
export function MediaSettingsCard({
  onDirtyChange,
}: MediaSettingsCardProps = {}): React.ReactElement | null {
  const { token, isAuthenticated } = useSession();
  const { showSuccess, showError } = useToast();
  const tenantSettings = useTenantSettings();

  // `current` mirrors the persisted server state; `draft` is the edited form.
  const [current, setCurrent] = useState<MediaFormState | null>(null);
  const [draft, setDraft] = useState<MediaFormState | null>(null);
  const [usage, setUsage] = useState<TenantMediaUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [conflict, setConflict] = useState<StoreSettingsConflict | null>(null);
  const restoreConflictFocusPendingRef = useRef(false);

  const patchDraft = useCallback((patch: Partial<MediaFormState>): void => {
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const toggleFormat = useCallback((format: AllowedMimeType): void => {
    setDraft((prev) => {
      if (!prev) return prev;
      const has = prev.allowedImageFormats.includes(format);
      const next = has
        ? prev.allowedImageFormats.filter((m) => m !== format)
        : FILE_VALIDATION.ALLOWED_MIME_TYPES.filter(
            (m) => m === format || prev.allowedImageFormats.includes(m)
          );
      return { ...prev, allowedImageFormats: next };
    });
  }, []);

  const restoreConflictFocus = useCallback(() => {
    const save = document.querySelector<HTMLButtonElement>('[data-testid="media-settings-save"]');
    if (save && !save.disabled) {
      save.focus();
      return;
    }
    document.querySelector<HTMLElement>('[data-testid="media-settings-enabled-toggle"]')?.focus();
  }, []);

  useEffect(() => {
    if (conflict !== null || !restoreConflictFocusPendingRef.current) return;
    restoreConflictFocusPendingRef.current = false;
    restoreConflictFocus();
  }, [conflict, restoreConflictFocus]);

  const applyObservedCurrent = useCallback(
    (observed: Partial<StoreSettingsExpectedState>): void => {
      setCurrent((previous) => {
        if (!previous) return previous;
        const next = { ...previous };
        const resolved = resolveMediaSettings(observed);
        if (Object.prototype.hasOwnProperty.call(observed, 'imageAttachmentsEnabled')) {
          next.imageAttachmentsEnabled = resolved.imageAttachmentsEnabled;
        }
        for (const field of NUMERIC_FIELDS) {
          if (!Object.prototype.hasOwnProperty.call(observed, field.key)) continue;
          const resolvedValue =
            field.key === 'maxImagesPerSubmission'
              ? resolved.maxImagesPerQuestion
              : resolved[field.key];
          next[field.key] = String(resolvedValue);
        }
        if (Object.prototype.hasOwnProperty.call(observed, 'allowedImageFormats')) {
          next.allowedImageFormats = [...resolved.allowedImageFormats];
        }
        if (Object.prototype.hasOwnProperty.call(observed, 'imageModeration')) {
          next.imageModeration = resolved.imageModeration;
        }
        return next;
      });
    },
    []
  );

  const handleSave = useCallback(
    async (
      expectedOverride?: Partial<StoreSettingsExpectedState>,
      payloadOverride?: StoreSettingsPatch
    ): Promise<boolean> => {
    if (!token || !current || !draft || !tenantSettings) return false;

    // Dirty-fields-only PUT (LaunchModeCard precedent): an unchanged field is
    // never re-sent. Numeric fields are already validated by the Save gate.
    const { payload: dirtyPayload, invalid } = buildDirtyPayload(current, draft);
    if (invalid || draft.allowedImageFormats.length === 0) return false;
    const payload = payloadOverride ?? dirtyPayload;
    if (Object.keys(payload).length === 0) return false;

    setSaving(true);
    try {
      const res = await tenantSettings.update(payload, expectedOverride);
      if (res.success) {
        // Re-seed from the response: the server returns EFFECTIVE values, so
        // an out-of-range hand-set value reads back clamped.
        const state = formStateFrom(res.data);
        setCurrent(state);
        setDraft(state);
        setUsage(res.data.mediaUsage);
        setConflict(null);
        showSuccess('Media settings saved.');
        return true;
        } else {
          const stale = readSettingsConflict(res.error);
          if (stale) {
            if (stale.recovery === 'direct') {
              tenantSettings.rebaseExpected(stale.observed);
              applyObservedCurrent(stale.observed);
            }
            setConflict(stale);
          } else if (isAmbiguousSettingsFailure(res.error)) {
            showError('Your change may have been saved — refresh to confirm.');
        } else {
          showError(res.error.message ?? 'Failed to update media settings');
        }
        return false;
      }
    } catch {
      showError('Your change may have been saved — refresh to confirm.');
      return false;
      } finally {
        setSaving(false);
      }
    },
    [token, current, draft, showSuccess, showError, tenantSettings, applyObservedCurrent]
  );

  const handleConflictReapply = useCallback(async () => {
    if (!conflict || !current || !draft || !tenantSettings) return;
    let baseline = current;
    let rawBaseline: Partial<StoreSettingsExpectedState> = conflict.observed;
    let expectedOverride: Partial<StoreSettingsExpectedState> = conflict.observed;
    if (conflict.recovery === 'refresh_required') {
      setSaving(true);
      const refreshed = await tenantSettings.refresh();
      if (!refreshed.success) {
        setSaving(false);
        showError('Could not refresh current settings. Your draft is preserved; try again.');
        return;
      }
      baseline = formStateFrom(refreshed.data);
      rawBaseline = refreshed.data.expectedState ?? {};
      expectedOverride = rawBaseline;
      setCurrent(baseline);
      setUsage(refreshed.data.mediaUsage);
    }

    const { payload: dirtyPayload, invalid } = buildDirtyPayload(baseline, draft);
    if (invalid || draft.allowedImageFormats.length === 0) {
      setSaving(false);
      showError('Correct the highlighted media settings before reapplying your draft.');
      return;
    }
    const recovery = { ...dirtyPayload } as Record<string, unknown>;
    for (const field of Object.keys(conflict.expected) as StoreSettingsWriteField[]) {
      const liveValue = mediaDraftValue(draft, field);
      if (JSON.stringify(rawBaseline[field]) === JSON.stringify(liveValue)) {
        delete recovery[field];
      } else {
        recovery[field] = liveValue;
      }
    }
    if (Object.keys(recovery).length === 0) {
      setSaving(false);
      restoreConflictFocusPendingRef.current = true;
      setConflict(null);
      return;
    }
    const recoveryFields = Object.keys(recovery) as StoreSettingsWriteField[];
    const usableExpected =
      conflict.recovery === 'direct'
        ? expectedOverride
        : selectUsableSettingsExpectedState(
            expectedOverride as StoreSettingsExpectedState,
            recoveryFields
          );
    if (!usableExpected) {
      setSaving(false);
      showError(
        'Current settings were refreshed, but a stored value needs operator repair before this draft can be reapplied.'
      );
      return;
    }
    restoreConflictFocusPendingRef.current = true;
    const saved = await handleSave(usableExpected, recovery as StoreSettingsPatch);
    if (!saved) restoreConflictFocusPendingRef.current = false;
  }, [conflict, current, draft, handleSave, showError, tenantSettings]);

  const dismissConflict = useCallback(() => {
    restoreConflictFocusPendingRef.current = true;
    setConflict(null);
  }, []);

  // Shares buildDirtyPayload with the Save handler (fold S4): dirty = any
  // changed key, INCLUDING a changed-but-invalid numeric (payload-empty +
  // invalid) — Save stays visible-but-disabled through `allValid`. Hoisted
  // above the auth guard (B-2) so the reporting hooks below run
  // unconditionally (hook order); null current/draft is never dirty.
  const dirtyState = current !== null && draft !== null ? buildDirtyPayload(current, draft) : null;
  const dirty =
    dirtyState !== null && (Object.keys(dirtyState.payload).length > 0 || dirtyState.invalid);

  const conflictNoticeDraft =
    conflict && draft
      ? Object.fromEntries(
          (Object.keys(conflict.expected) as StoreSettingsWriteField[]).map((field) => [
            field,
            mediaDraftValue(draft, field),
          ])
        )
      : {};

  useEffect(() => {
    if (!tenantSettings) return;
    setLoading(tenantSettings.status === 'loading');
    setLoadError(tenantSettings.status === 'error');
    if (tenantSettings.status !== 'ready' || !tenantSettings.settings || dirty) return;
    const state = formStateFrom(tenantSettings.settings);
    setCurrent(state);
    setDraft(state);
    setUsage(tenantSettings.settings.mediaUsage);
  }, [dirty, tenantSettings]);
  // Report dirty TRANSITIONS to the optional shell listener (ADR-025 B-2 /
  // §3.1.4) — never per re-render; behaviourally inert without the prop, so
  // the legacy layout renders this card unchanged. Callback via ref so a new
  // identity per parent render never re-fires the effect.
  const onDirtyChangeRef = useRef(onDirtyChange);
  useEffect(() => {
    onDirtyChangeRef.current = onDirtyChange;
  }, [onDirtyChange]);
  const lastReportedDirty = useRef(false);
  useEffect(() => {
    if (lastReportedDirty.current === dirty) return;
    lastReportedDirty.current = dirty;
    onDirtyChangeRef.current?.(dirty);
  }, [dirty]);
  // A card that is gone is not dirty (defensive — the shell keeps Settings
  // keep-alive-mounted; the indicator must not stick if a future layout
  // unmounts a dirty card).
  useEffect(
    () => () => {
      if (lastReportedDirty.current) onDirtyChangeRef.current?.(false);
    },
    []
  );

  if (!isAuthenticated || !token) {
    return null;
  }

  // Per-field numeric validity (UX mirror of the server's resolver bounds).
  const numericErrors: Partial<Record<NumericFieldKey, string>> = {};
  if (draft) {
    for (const field of NUMERIC_FIELDS) {
      if (parseBoundedInt(draft[field.key], field.min, field.max) === null) {
        numericErrors[field.key] = `Enter a whole number from ${field.min} to ${field.max}.`;
      }
    }
  }
  const formatsValid = (draft?.allowedImageFormats.length ?? 0) > 0;
  const allValid = Object.keys(numericErrors).length === 0 && formatsValid;

  return (
    <Panel header="Image attachments" data-testid="media-settings-card">
      <SettingsConflictNotice
        conflict={conflict}
        draft={conflictNoticeDraft}
        labels={{
          imageAttachmentsEnabled: 'Allow image attachments',
          maxImagesPerSubmission: 'Max images per question',
          maxImagesPerReply: 'Max images per customer reply',
          maxImagesPerStaffReply: 'Max images per staff reply',
          maxImageSizeMb: 'Max image size',
          allowedImageFormats: 'Allowed formats',
          imageModeration: 'Customer image moderation',
        }}
        formatValue={(field: StoreSettingsWriteField, value: unknown) => {
          if (typeof value === 'boolean') return value ? 'Enabled' : 'Disabled';
          if (field === 'allowedImageFormats' && Array.isArray(value)) {
            return value
              .map((format) => FORMAT_LABELS[format as AllowedMimeType] ?? format)
              .join(', ');
          }
          if (field === 'imageModeration' && typeof value === 'string') {
            return POSTURE_LABELS[value as ImageModerationPosture] ?? value;
          }
          return value === null || value === undefined ? 'Not explicitly set' : String(value);
        }}
        busy={saving}
        onReapply={() => {
          void handleConflictReapply();
        }}
        onDismiss={dismissConflict}
      />
      {loading ? (
        <Text>Loading…</Text>
      ) : loadError || !draft ? (
        <Text color="danger">Could not load the current media settings.</Text>
      ) : (
        <>
          {/* Master toggle — off means off for EVERY author, staff included
              (owner-approved fork 2026-07-04); management of existing media
              stays available. */}
          <Box marginBottom="medium" data-testid="media-settings-enabled-section">
            <Flex alignItems="center" flexGap="12px" marginBottom="xxSmall">
              <FlexItem flexGrow={0}>
                <Switch
                  checked={draft.imageAttachmentsEnabled}
                  disabled={saving}
                  onChange={(e) => patchDraft({ imageAttachmentsEnabled: e.target.checked })}
                  data-testid="media-settings-enabled-toggle"
                />
              </FlexItem>
              <FlexItem flexGrow={1}>
                <Text bold marginBottom="none">
                  Allow image attachments
                </Text>
              </FlexItem>
            </Flex>
            <Text color="secondary" marginBottom="none">
              When off, no one can upload new images — customers or staff. Existing images stay
              manageable in the review queue.
            </Text>
          </Box>

          <Flex flexGap="1rem" flexWrap="wrap" marginBottom="medium">
            {NUMERIC_FIELDS.map((field) => (
              <FlexItem key={field.key} style={{ width: '220px' }}>
                {/* FormGroup is what renders the Input's error text (AuditPanel
                    precedent — a bare Input swallows `error`). */}
                <FormGroup>
                  <Input
                    label={field.label}
                    type="number"
                    min={field.min}
                    max={field.max}
                    value={draft[field.key]}
                    disabled={saving}
                    onChange={(e) => patchDraft({ [field.key]: e.target.value })}
                    description={field.help}
                    error={numericErrors[field.key]}
                    data-testid={`media-settings-${field.key}`}
                  />
                </FormGroup>
              </FlexItem>
            ))}
          </Flex>

          <Box marginBottom="medium" data-testid="media-settings-formats">
            <Text bold marginBottom="xxSmall">
              Allowed formats
            </Text>
            <Flex flexGap="1rem" flexWrap="wrap">
              {FILE_VALIDATION.ALLOWED_MIME_TYPES.map((format) => (
                <FlexItem key={format}>
                  <Checkbox
                    label={FORMAT_LABELS[format]}
                    checked={draft.allowedImageFormats.includes(format)}
                    disabled={saving}
                    onChange={() => toggleFormat(format)}
                    data-testid={`media-settings-format-${FORMAT_LABELS[format].toLowerCase()}`}
                  />
                </FlexItem>
              ))}
            </Flex>
            {!formatsValid && (
              <Text color="danger" marginBottom="none" data-testid="media-settings-formats-error">
                Select at least one format.
              </Text>
            )}
          </Box>

          <Box marginBottom="medium" style={{ maxWidth: '320px' }}>
            <Select
              label="Customer image moderation"
              options={POSTURE_OPTIONS}
              value={draft.imageModeration}
              disabled={saving}
              onOptionChange={(value: ImageModerationPosture) =>
                patchDraft({ imageModeration: value })
              }
              data-testid="media-settings-moderation"
            />
            <Text
              color="secondary"
              marginBottom="none"
              data-testid="media-settings-moderation-description"
            >
              {POSTURE_DESCRIPTIONS[draft.imageModeration]} Staff uploads are always published
              immediately, regardless of this setting.
            </Text>
          </Box>

          {/* Read-only D-QUOTA usage (ADR-022 §14 — ceilings are platform
              constants; the tenant-tunable override is deferred). */}
          {usage && (
            <Box marginBottom="medium" data-testid="media-settings-usage">
              <Text bold marginBottom="xxSmall">
                Storage used
              </Text>
              <Text color="secondary" marginBottom="none">
                {usage.attachments.toLocaleString()} of {usage.maxAttachments.toLocaleString()}{' '}
                images · {formatBytesHuman(usage.bytes)} of {formatBytesHuman(usage.maxBytes)}
              </Text>
            </Box>
          )}

          <Flex>
            <FlexItem>
              <Button
                onClick={() => {
                  void handleSave();
                }}
                disabled={!dirty || !allValid || saving}
                isLoading={saving}
                data-testid="media-settings-save"
              >
                Save
              </Button>
            </FlexItem>
          </Flex>
        </>
      )}
    </Panel>
  );
}
